import Button, { type ButtonProps } from '@pelckmans/elementis/components/button';
import ButtonDropdown from '@pelckmans/elementis/components/button-dropdown';
import DigibookIcon from '@pelckmans/elementis/icons/digibook';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import AccountContext from '../../../contexts/AccountContext';
import { type App } from '../api/appsApi';
import { buildCodexUrl, getDigibookTooltip, getDisabledState } from '../utils/digibookAppUtils';

const getHref = (digibookApp: App, accountProvider?: string) => {
  if (digibookApp.platformUrl) return digibookApp.platformUrl;
  if (digibookApp.linkedApp)
    return buildCodexUrl({
      digibookId: digibookApp.linkedApp.id,
      moduleId: digibookApp.moduleId,
      accountProvider,
    });
};

export default function DigibookButton({ digibookApps, size }: { digibookApps: App[]; size: ButtonProps['size'] }) {
  const { t } = useTranslation();
  const { account } = useContext(AccountContext);

  if (digibookApps.length === 0) return null;

  if (digibookApps.length === 1) {
    const digibookApp = digibookApps[0];
    const disabledState = getDisabledState(digibookApp);
    const href = getHref(digibookApp, account?.provider) || '';

    return (
      <Button
        key={digibookApp.id}
        as="link"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        prominence="secondary"
        size={size}
        startIcon={<DigibookIcon data-testid="digibook-icon" />}
        disabled={disabledState !== 'notDisabled'}
        tooltip={disabledState !== 'notDisabled' ? t(getDigibookTooltip(disabledState)) : undefined}
      >
        {t('PAGES.TOC_LEVEL.APPS.DIGIBOOK.OPEN_DIGIBOOK')}
      </Button>
    );
  }

  return (
    <ButtonDropdown
      title={t('PAGES.TOC_LEVEL.APPS.DIGIBOOK.OPEN_DIGIBOOK')}
      size={size}
      key={digibookApps[0].id}
      buttonProps={{
        prominence: 'secondary',
        startIcon: <DigibookIcon data-testid="digibook-icon" />,
      }}
    >
      {digibookApps.map(digibookApp => {
        const hasNoLicense = getDisabledState(digibookApp);
        const disabledProps =
          hasNoLicense !== 'notDisabled'
            ? ({ disabled: true, tooltip: t(getDigibookTooltip(hasNoLicense)) } as const)
            : {};

        return (
          <ButtonDropdown.Item
            as="link"
            href={getHref(digibookApp, account?.provider)}
            target="_blank"
            rel="noopener noreferrer"
            key={digibookApp.id}
            value={digibookApp.id}
            text={`${digibookApp.module.niceName}: ${digibookApp.name}`}
            {...disabledProps}
          />
        );
      })}
    </ButtonDropdown>
  );
}
